import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import {
  findByPermalink,
  galleryItemsQueryOptions,
  itemTitle,
  type GalleryItem,
} from "@/lib/gallery.queries";
import { VideoLightbox } from "@/components/site/VideoLightbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/gallery/$permalink")({
  loader: async ({ context, params }) => {
    const items = await context.queryClient.ensureQueryData(
      galleryItemsQueryOptions("gallery-video"),
    );
    const item = findByPermalink(items, params.permalink);
    if (!item) throw notFound();
    return { item };
  },
  head: ({ loaderData }) => {
    const item = loaderData?.item as GalleryItem | undefined;
    if (!item) return {};
    const title = `${itemTitle(item, "Video")} — Gallery — Medical Insight`;
    const description =
      item.description?.slice(0, 200) ??
      "Video from the Medical Insight gallery.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        ...(item.poster_path
          ? [{ property: "og:image", content: item.poster_path }]
          : []),
      ],
    };
  },
  notFoundComponent: VideoNotFound,
  component: GalleryVideoRoute,
});

function GalleryVideoRoute() {
  const { item } = Route.useLoaderData();
  const navigate = useNavigate();

  return (
    <VideoLightbox
      item={item}
      open
      basePath="/gallery"
      kicker="Gallery"
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: "/gallery",
            search: (prev) => ({ ...prev, video: undefined }),
            replace: true,
          });
        }
      }}
    />
  );
}

function VideoNotFound() {
  const navigate = useNavigate();
  const back = () =>
    navigate({ to: "/gallery", search: (prev) => ({ ...prev, video: undefined }), replace: true });
  return (
    <Dialog open onOpenChange={(open) => !open && back()}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pr-10 text-left">
          <DialogTitle>Video not found</DialogTitle>
          <DialogDescription>
            This video is no longer available or the link is incorrect.
          </DialogDescription>
        </DialogHeader>
        <Button onClick={back}>Back to the gallery</Button>
      </DialogContent>
    </Dialog>
  );
}
